import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { findIndex, has, sortBy } from 'lodash';
import { BlockEnum } from '../../../common/enum';
import { LayoutTemplate } from '../../../views/book-create/BookEditor/Page/utils';
import { PageTemplateItem } from '../../../views/book-create/drawer-left-menu/Design/PageTemplate/PageTemplate';
import { TemplateFilters } from '../../../views/dashboard/TemplateSection/TemplateSection';
import { ArchivedBookDTO, BlockDTO, BookDTO, PageDTO, PageLayout, TextSetting } from '../../types/apiType';
import { BookHistory, CopiedBlockStyle, HistoryActionEnum, SyncStatus } from '../../types/common';

export interface DimensionPage {
  ratio: number;
  pageWidth?: number;
  pageHeight?: number;
}

export interface ViewControlStatus {
  currentPage: number;
  pages: 1 | 2;
  zoom: number;
  drawerLeftMenuContent: string;
  currentScale: number;
  currentScaleCover: number;
}

export interface BookState {
  activePageId: string;

  pages: PageDTO[];
  currentBook: BookDTO | null;
  cursor: number;
  viewControlStatus: ViewControlStatus;
  drawerLeftMenuContent: string;

  bookHistory: {
    past: BookHistory[];
    future: BookHistory[];
  };

  myBooks: BookDTO[];
  bookInvitedTo: BookDTO[];
  archivedBooks: ArchivedBookDTO[];
  templatedBooks: ArchivedBookDTO[];
  focusBlock: BlockDTO | null;
  dimensionPage: DimensionPage;
  dimensionCover: DimensionPage;
  bookLoading: string;
  selectedBookId: string;
  isInteractiveProgress: boolean;
  isCoverPage: boolean;
  selectedCoverComponent: string;
  coverLayout: any | null;
  currentPagesLayout: PageLayout[];
  isShowDropArea: boolean;
  isShowLayoutsMenu: boolean;
  isRefreshingLayerPosition: boolean;
  isRefreshingNavigatorPosition: boolean;
  imageModalAction: string;
  flipState: string;
  pageLoading: string;
  refreshEditor: string;
  blockLoading: string;
  blockDataHistory: { history: BlockDTO[]; state: number };
  selectedBook: BookDTO | null;
  isMainBlockMoveable: boolean;
  isLayoutBlockMoveable: boolean;
  isMainImageMoveable: boolean;
  updateStyle: string;
  templateFilters: TemplateFilters;
  selectedTemplate: ArchivedBookDTO | null;
  pageSystemTemplate: ArchivedBookDTO[];
  bookSystemTemplate: ArchivedBookDTO[];
  isCommentPanelOpen: boolean;
  selectedCommentThread: string;
  syncStatus: SyncStatus;
  confirmReplaceLayout: LayoutTemplate | null;
  confirmApplyTemplate: PageTemplateItem | null;
  selectedBlockIds: string[];
  isSelectingBlock: boolean;
  copiedBlockStyle: CopiedBlockStyle | null;
  isExpandTabletMenu: boolean;
  isExpandTabletMenuDetail: boolean;
}

const initialState: BookState = {
  activePageId: '',

  bookHistory: {
    past: [],
    future: [],
  },

  currentBook: null,
  myBooks: [],
  archivedBooks: [],
  templatedBooks: [],
  focusBlock: null,
  pages: [],
  cursor: 1,
  dimensionPage: { ratio: 1 },
  dimensionCover: { ratio: 1 },
  bookLoading: '',
  selectedBookId: '',
  isInteractiveProgress: false,
  bookInvitedTo: [],
  isCoverPage: false,
  selectedCoverComponent: '',
  coverLayout: null,
  currentPagesLayout: [],
  viewControlStatus: {
    currentPage: 1,
    pages: 2,
    zoom: 100,
    currentScale: 1,
    drawerLeftMenuContent: '',
    currentScaleCover: 1,
  },
  drawerLeftMenuContent: '',
  isShowDropArea: false,
  isShowLayoutsMenu: false,
  isRefreshingLayerPosition: false,
  isRefreshingNavigatorPosition: false,
  imageModalAction: '',
  flipState: '',
  pageLoading: '',
  blockLoading: '',
  refreshEditor: '',
  blockDataHistory: { history: [], state: 0 },
  selectedBook: null,
  isMainBlockMoveable: true,
  isLayoutBlockMoveable: true,
  isMainImageMoveable: false,
  updateStyle: '',
  templateFilters: { categories: [], styles: [], orientation: [], size: [], colour: [] },
  selectedTemplate: null,
  pageSystemTemplate: [],
  bookSystemTemplate: [],
  isCommentPanelOpen: false,
  selectedCommentThread: '',
  syncStatus: SyncStatus.SUCCESS,
  confirmReplaceLayout: null,
  confirmApplyTemplate: null,
  selectedBlockIds: [],
  isSelectingBlock: false,
  copiedBlockStyle: null,
  isExpandTabletMenu: false,
  isExpandTabletMenuDetail: false,
};

export const bookSlice = createSlice({
  name: 'book',
  initialState,
  reducers: {
    setBookHistory: (
      state,
      action: PayloadAction<{
        past: BookHistory[];
        future: BookHistory[];
      }>,
    ) => {
      state.bookHistory = action.payload;
    },
    updateBookHistory: (state, action: PayloadAction<{ action?: HistoryActionEnum }>) => {
      const { action: a } = action.payload;
      const { past } = state.bookHistory;
      const present = { ...state.currentBook, pages: state.pages } as BookHistory;
      if (!a) {
        state.bookHistory = {
          past: past.length >= 5 ? [...past.slice(past.length - 4, past.length), present] : [...past, present],
          future: [],
        };
      }

      const { future } = state.bookHistory;
      switch (a) {
        case HistoryActionEnum.UNDO:
          const previous = past[past.length - 1];
          if (!previous) return;
          const newPast = past.slice(0, past.length - 1);

          state.bookHistory = {
            past: [...newPast],
            future: [present, ...future],
          };
          break;
        case HistoryActionEnum.REDO:
          const next = future[0];
          if (!next) return;
          const newFuture = future.slice(1, future.length);

          state.bookHistory = {
            past: [...past, present],
            future: [...newFuture],
          };
          break;
        default:
          break;
      }
    },
    setBlockDataHistory: (state, action: PayloadAction<{ history: BlockDTO[]; state: number }>) => {
      state.blockDataHistory = action.payload;
    },
    setDrawerLeftMenuContent: (state, action: PayloadAction<string>) => {
      state.drawerLeftMenuContent = action.payload;
    },

    setConfirmReplaceLayout: (state, action: PayloadAction<LayoutTemplate | null>) => {
      state.confirmReplaceLayout = action.payload;
    },
    setConfirmApplyTemplate: (state, action: PayloadAction<PageTemplateItem | null>) => {
      state.confirmApplyTemplate = action.payload;
    },
    setSyncStatus: (state, action: PayloadAction<SyncStatus>) => {
      state.syncStatus = action.payload;
    },
    setSelectedCommentThread: (state, action: PayloadAction<string | null | undefined>) => {
      state.selectedCommentThread = action.payload || '';
    },

    setPageSystemTemplates: (state, action: PayloadAction<ArchivedBookDTO[]>) => {
      state.pageSystemTemplate = action.payload;
    },
    setBookSystemTemplates: (state, action: PayloadAction<ArchivedBookDTO[]>) => {
      state.bookSystemTemplate = action.payload;
    },
    setSelectedTemplate: (state, action: PayloadAction<ArchivedBookDTO | null>) => {
      state.selectedTemplate = action.payload;
    },
    setTemplateFilters: (state, action: PayloadAction<Partial<TemplateFilters>>) => {
      state.templateFilters = { ...state.templateFilters, ...action.payload };
    },
    setSelectedBook: (state, action: PayloadAction<BookDTO | null>) => {
      state.selectedBook = action.payload;
    },
    setIsMainBlockMoveable: (state, action: PayloadAction<boolean>) => {
      state.isMainBlockMoveable = action.payload;
    },
    setIsLayoutBlockMoveable: (state, action: PayloadAction<boolean>) => {
      state.isLayoutBlockMoveable = action.payload;
    },
    setIsMainImageMoveable: (state, action: PayloadAction<boolean>) => {
      state.isMainImageMoveable = action.payload;
    },
    setBookLoading: (state, action: PayloadAction<string>) => {
      state.bookLoading = action.payload;
    },
    setPageLoading: (state, action: PayloadAction<string>) => {
      state.pageLoading = action.payload;
    },
    setUpdateStyle: (state, action: PayloadAction<string>) => {
      state.updateStyle = action.payload;
    },
    setBlock: (state, action: PayloadAction<Partial<BlockDTO> | BlockDTO>) => {
      if (!action.payload.id || !action.payload.pageId) return state;
      const key = { id: action.payload.id, pageId: action.payload.pageId };
      const pageIndex = state?.pages.findIndex((page) => page.id === key.pageId);
      const blockIndex = state?.pages[pageIndex]?.blocks?.findIndex((block) => block.id === key.id) ?? -1;

      const present = state?.pages[pageIndex]?.blocks[blockIndex];

      if (!present) return state;

      state.pages[pageIndex].blocks[blockIndex] = { ...present, ...action.payload };
    },
    setCurrentBook: (state, action: PayloadAction<BookDTO | null>) => {
      state.currentBook = action.payload;
    },
    updateCurrentBook: (state, action: PayloadAction<Partial<BookDTO>>) => {
      if (state.currentBook && state.currentBook.id === action.payload.id) {
        state.currentBook = { ...state.currentBook, ...action.payload };
      }
    },
    setIsShowDropArea: (state, action: PayloadAction<boolean>) => {
      state.isShowDropArea = action.payload;
    },
    setCoverLayout: (state, action: PayloadAction<any | null>) => {
      state.coverLayout = action.payload;
    },
    setBook: (state, action: PayloadAction<Partial<BookDTO>>) => {
      if (state.currentBook && state.currentBook.id === action.payload.id) {
        state.currentBook = { ...state.currentBook, ...action.payload };
      }
    },
    setMyBooks: (state, action: PayloadAction<BookDTO[]>) => {
      state.myBooks = action.payload;
    },
    setBookInvitedTo: (state, action: PayloadAction<BookDTO[]>) => {
      state.bookInvitedTo = action.payload;
    },
    setArchivedBooks: (state, action: PayloadAction<ArchivedBookDTO[]>) => {
      state.archivedBooks = action.payload;
    },
    setTemplateBooks: (state, action: PayloadAction<ArchivedBookDTO[]>) => {
      state.templatedBooks = action.payload;
    },
    setPages: (state, action: PayloadAction<PageDTO[]>) => {
      state.pages = action.payload;
    },
    updatePages: (state, action: PayloadAction<PageDTO[]>) => {
      const ids = action.payload.map((p) => p.id);
      state.pages = sortBy([...state.pages.filter((p) => !ids.includes(p.id)), ...action.payload], ['index']);
    },
    setPage: (state, action: PayloadAction<Partial<PageDTO>>) => {
      const index = findIndex(state.pages, (page: PageDTO) => page.id === action.payload.id);
      if (index >= 0 && state.pages[index] && state.pages[index].bookId === state.currentBook?.id) {
        state.pages[index] = { ...state.pages[index], ...action.payload };
      }
    },
    deleteBlocks: (state, action: PayloadAction<{ bookId: string; pageId: string; ids: string[] }>) => {
      const index = findIndex(state.pages, (page: PageDTO) => page.id === action.payload.pageId);
      if (index >= 0 && state.pages[index] && state.pages[index].bookId === state.currentBook?.id) {
        const blocks = state.pages[index].blocks.filter((b) => !action.payload.ids.includes(b.id));
        state.pages[index].blocks = blocks;
      }
    },
    updateBlocksPage: (state, action: PayloadAction<{ id: string; blocks: BlockDTO[] }>) => {
      const index = findIndex(state.pages, (page: PageDTO) => page.id === action.payload.id);
      if (index >= 0 && state.pages[index] && state.pages[index].bookId === state.currentBook?.id) {
        const currBlocks = state.pages[index].blocks;

        state.pages[index].blocks = [...currBlocks, ...action.payload.blocks];
      }
    },
    setCursor: (state, action: PayloadAction<number>) => {
      state.cursor = action.payload;
    },
    setFocusBlock: (state, action: PayloadAction<Partial<BlockDTO> | null>) => {
      if (!action.payload) {
        state.focusBlock = action.payload || null;
        return;
      }
      if (!action.payload.id || !action.payload.pageId) return state;
      const key = { id: action.payload.id, pageId: action.payload.pageId };
      const pageIndex = state?.pages.findIndex((page) => page.id === key.pageId);
      const blockIndex = state?.pages[pageIndex]?.blocks?.findIndex((block) => block.id === key.id) ?? -1;

      const present = state?.pages[pageIndex]?.blocks[blockIndex];

      if (!present) return state;
      state.focusBlock = { ...present, ...action.payload };
      state.pages[pageIndex].blocks[blockIndex] = { ...present, ...action.payload };
    },
    setSelectedBlockIds: (state, action: PayloadAction<string[]>) => {
      state.selectedBlockIds = action.payload;
    },
    setDimensionPage: (state, action: PayloadAction<DimensionPage>) => {
      state.dimensionPage = action.payload;
    },
    setDimensionCover: (state, action: PayloadAction<DimensionPage>) => {
      state.dimensionCover = action.payload;
    },
    setBlockLoading: (state, action: PayloadAction<string>) => {
      state.blockLoading = action.payload;
    },
    setDefaultSetting: (state, action: PayloadAction<{ setting: Partial<TextSetting>; type: BlockEnum }>) => {
      const { setting, type } = action.payload;
      if (type === BlockEnum.TITLE && state.currentBook?.defaultSetting) {
        state.currentBook = {
          ...state.currentBook,
          defaultSetting: {
            ...state.currentBook?.defaultSetting,
            pageTitleSetting: { ...state.currentBook.defaultSetting.pageTitleSetting, ...setting },
          },
        };
      }

      if (type === BlockEnum.PARAGRAPH && state.currentBook?.defaultSetting) {
        state.currentBook = {
          ...state.currentBook,
          defaultSetting: {
            ...state.currentBook?.defaultSetting,
            mainTextSetting: { ...state.currentBook.defaultSetting.mainTextSetting, ...setting },
          },
        };
      }
    },
    setIsInteractiveProgress: (state, action: PayloadAction<boolean>) => {
      state.isInteractiveProgress = action.payload;
    },
    setIsShowLayoutsMenu: (state, action: PayloadAction<boolean>) => {
      state.isShowLayoutsMenu = action.payload;
    },
    setIsCoverPage: (state, action: PayloadAction<boolean>) => {
      state.isCoverPage = action.payload;
    },
    setSelectedCoverComponent: (state, action: PayloadAction<string>) => {
      state.selectedCoverComponent = action.payload;
    },
    setViewControlStatus: (state, action: PayloadAction<Partial<ViewControlStatus>>) => {
      state.viewControlStatus = { ...state.viewControlStatus, ...action.payload };
    },
    setPageLayout: (state, action: PayloadAction<Partial<PageLayout>>) => {
      if (action.payload.pageId) {
        const existLayout: PageLayout | undefined = state.currentPagesLayout.find(
          (layout) => layout.pageId === action.payload.pageId && layout.zIndex === action.payload.zIndex,
        ) || { pageId: action.payload.pageId, opacity: 100, zIndex: 0 };

        const updatedLayout = { ...existLayout, ...action.payload };

        if (has(action.payload, 'isSelected')) {
          state.currentPagesLayout = [
            ...state.currentPagesLayout
              .filter((layout) => !(layout.pageId === action.payload.pageId && layout.zIndex === action.payload.zIndex))
              .map((layout) => (layout.pageId === action.payload.pageId ? { ...layout, isSelected: false } : layout)),
            { ...updatedLayout },
          ];
        } else {
          state.currentPagesLayout = [
            ...state.currentPagesLayout.filter(
              (layout) => !(layout.pageId === action.payload.pageId && layout.zIndex === action.payload.zIndex),
            ),
            { ...updatedLayout },
          ];
        }
      }
    },
    setActivePageId: (state, action: PayloadAction<string>) => {
      state.activePageId = action.payload;
    },
    setActivePageIdByIndex: (state, action: PayloadAction<number>) => {
      const page = state.pages.find((p) => p.index === action.payload);
      if (!page) return;
      state.focusBlock = null;
      state.activePageId = page.id;
    },
    setIsRefreshingLayerPosition: (state, action: PayloadAction<boolean>) => {
      state.isRefreshingLayerPosition = action.payload;
    },
    setIsRefreshingNavigatorPosition: (state, action: PayloadAction<boolean>) => {
      state.isRefreshingNavigatorPosition = action.payload;
    },
    setSelectedImageModalAction: (state, action: PayloadAction<string>) => {
      state.imageModalAction = action.payload;
    },
    setFlipState: (state, action: PayloadAction<string>) => {
      state.flipState = action.payload;
    },
    setRefreshEditor: (state, action: PayloadAction<string>) => {
      state.refreshEditor = action.payload;
    },
    setIsCommentPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isCommentPanelOpen = action.payload;
    },
    triggerSelectorBlock: (state, action: PayloadAction<boolean>) => {
      state.isSelectingBlock = action.payload;
    },
    setCopiedBlockStyle: (state, action: PayloadAction<CopiedBlockStyle | null>) => {
      state.copiedBlockStyle = action.payload;
    },
    setIsExpandTabletMenu: (state, action: PayloadAction<boolean>) => {
      state.isExpandTabletMenu = action.payload;
    },
    setIsExpandTabletMenuDetail: (state, action: PayloadAction<boolean>) => {
      state.isExpandTabletMenuDetail = action.payload;
    },
  },
});

export const {
  setIsExpandTabletMenu,
  setIsExpandTabletMenuDetail,
  setCopiedBlockStyle,
  updatePages,
  triggerSelectorBlock,
  setBookHistory,
  updateBookHistory,
  setIsLayoutBlockMoveable,
  setDrawerLeftMenuContent,
  setSelectedTemplate,
  setIsMainBlockMoveable,
  setIsMainImageMoveable,
  setSelectedBook,
  setBlockDataHistory,
  setTemplateFilters,
  setBlockLoading,
  setRefreshEditor,
  setIsRefreshingNavigatorPosition,
  setIsRefreshingLayerPosition,
  setIsShowLayoutsMenu,
  setViewControlStatus,
  setCoverLayout,
  setBookLoading,
  setPageLoading,
  setArchivedBooks,
  setMyBooks,
  setActivePageIdByIndex,
  setCurrentBook,
  updateCurrentBook,
  setBook,
  setPages,
  setPage,
  setCursor,
  setFocusBlock,
  setDimensionPage,
  setDimensionCover,
  setDefaultSetting,
  setBlock,
  setIsInteractiveProgress,
  setBookInvitedTo,
  setIsCoverPage,
  setSelectedCoverComponent,
  setPageLayout,
  setIsShowDropArea,
  setActivePageId,
  setSelectedImageModalAction,
  updateBlocksPage,
  setFlipState,
  setUpdateStyle,
  setTemplateBooks,
  setPageSystemTemplates,
  setBookSystemTemplates,
  setIsCommentPanelOpen,
  setSelectedCommentThread,
  setSyncStatus,
  setSelectedBlockIds,
  setConfirmReplaceLayout,
  setConfirmApplyTemplate,
  deleteBlocks,
} = bookSlice.actions;

export default bookSlice.reducer;
